import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  Plus, Filter, MapPin, Clock, User, FileText,
  CheckCircle, AlertCircle, Edit2, Trash2, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Updated event types: merged Livraison + Pose
const EVENT_TYPES = [
  { value: 'mesure', label: 'Relevé de mesures', color: '#3B82F6' },
  { value: 'livraison_pose', label: 'Livraison & Pose', color: '#10B981' },
  { value: 'autre', label: 'Autre', color: '#8B5CF6' },
];

const STATUS_LABELS = {
  proposed: 'Date proposée',
  validated: 'Date validée',
  modified_by_admin: 'Modifiée par admin',
  pending: 'En attente'
};

export default function AdminPlanning() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterType, setFilterType] = useState('all');
  const [filterPro, setFilterPro] = useState('all');
  const [proUsers, setProUsers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  
  // Edit Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editForm, setEditForm] = useState({
    validated_date: '',
    validated_time: '',
    status: '',
    comment: ''
  });

  // Add Event Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newEventForm, setNewEventForm] = useState({
    quote_id: '',
    event_type: '',
    event_label: '',
    proposed_date: '',
    proposed_time: '',
    comment: ''
  });

  useEffect(() => {
    fetchEvents();
    fetchProUsers();
    fetchQuotes();
  }, [currentDate, filterType, filterPro]);

  const fetchEvents = async () => {
    try {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      let url = `${API_URL}/planning/events?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`;
      
      if (filterType !== 'all') {
        url += `&event_type=${filterType}`;
      }
      if (filterPro !== 'all') {
        url += `&pro_user_id=${filterPro}`;
      }
      
      const response = await axios.get(url);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setProUsers(response.data.filter(u => u.role === 'pro'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleValidateEvent = async (eventId, newDate) => {
    try {
      await axios.put(`${API_URL}/planning/events/${eventId}`, {
        validated_date: newDate || selectedEvent.proposed_date,
        status: 'validated'
      });
      toast.success('Date validée');
      fetchEvents();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      // Combine date and time
      let validated_date = editForm.validated_date;
      if (editForm.validated_time) {
        validated_date = `${editForm.validated_date}T${editForm.validated_time}:00`;
      }
      
      await axios.put(`${API_URL}/planning/events/${selectedEvent.id}`, {
        validated_date,
        status: editForm.status,
        comment: editForm.comment
      });
      toast.success('Événement mis à jour');
      fetchEvents();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    
    try {
      await axios.delete(`${API_URL}/planning/events/${eventId}`);
      toast.success('Événement supprimé');
      fetchEvents();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddEvent = async () => {
    // Validation for "Autre" type
    if (newEventForm.event_type === 'autre' && !newEventForm.event_label.trim()) {
      toast.error('Veuillez préciser le type d\'événement');
      return;
    }
    
    if (!newEventForm.event_type || !newEventForm.proposed_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      // Combine date and time
      let proposed_date = newEventForm.proposed_date;
      if (newEventForm.proposed_time) {
        proposed_date = `${newEventForm.proposed_date}T${newEventForm.proposed_time}:00`;
      }
      
      await axios.post(`${API_URL}/planning/events`, {
        quote_id: newEventForm.quote_id === 'none' ? null : (newEventForm.quote_id || null),
        event_type: newEventForm.event_type,
        event_label: newEventForm.event_label,
        proposed_date,
        comment: newEventForm.comment
      });
      
      toast.success('Événement créé');
      fetchEvents();
      setIsAddDialogOpen(false);
      setNewEventForm({
        quote_id: '',
        event_type: '',
        event_label: '',
        proposed_date: '',
        proposed_time: '',
        comment: ''
      });
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const openEventDialog = (event) => {
    setSelectedEvent(event);
    const dateStr = event.validated_date || event.proposed_date || '';
    const datePart = dateStr.split('T')[0] || '';
    const timePart = dateStr.includes('T') ? dateStr.split('T')[1]?.substring(0, 5) : '';
    
    setEditForm({
      validated_date: datePart,
      validated_time: timePart,
      status: event.status || 'proposed',
      comment: event.comment || ''
    });
    setIsDialogOpen(true);
  };

  // Calendar helpers
  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty cells for days before first day of month (Monday start)
    const startDay = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add all days of month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, [currentDate]);

  const getEventsForDay = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => {
      const eventDate = e.validated_date || e.proposed_date;
      return eventDate?.split('T')[0] === dateStr;
    });
  };

  const getEventTypeInfo = (event) => {
    // Map old types to new merged type
    const type = event.event_type === 'pose' || event.event_type === 'livraison' 
      ? 'livraison_pose' 
      : event.event_type;
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[2];
  };

  const getEventDisplayLabel = (event) => {
    if (event.event_type === 'autre' && event.event_label) {
      return event.event_label;
    }
    const typeInfo = getEventTypeInfo(event);
    return typeInfo.label;
  };

  const getEventTime = (event) => {
    const dateStr = event.validated_date || event.proposed_date;
    if (dateStr?.includes('T')) {
      return dateStr.split('T')[1]?.substring(0, 5);
    }
    return null;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="space-y-6" data-testid="admin-planning">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Planning des interventions</h1>
          <p className="text-white/50 mt-1">Gérez les dates de relevés, livraisons et poses</p>
        </div>
        <button
          onClick={() => setIsAddDialogOpen(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-event-btn"
        >
          <Plus className="w-4 h-4" />
          Ajouter un événement
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center p-4 bg-[#1C1C1C] border border-white/10">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/50" />
          <span className="text-white/50 text-sm">Filtres:</span>
        </div>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 bg-[#0A0A0A] border-white/10 text-white rounded-none">
            <SelectValue placeholder="Type d'événement" />
          </SelectTrigger>
          <SelectContent className="bg-[#1C1C1C] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">Tous les types</SelectItem>
            {EVENT_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                  {type.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterPro} onValueChange={setFilterPro}>
          <SelectTrigger className="w-48 bg-[#0A0A0A] border-white/10 text-white rounded-none">
            <SelectValue placeholder="Client pro" />
          </SelectTrigger>
          <SelectContent className="bg-[#1C1C1C] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">Tous les clients</SelectItem>
            {proUsers.map(user => (
              <SelectItem key={user.id} value={user.id} className="text-white hover:bg-white/10">
                {user.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Legend */}
        <div className="flex items-center gap-4 ml-auto">
          {EVENT_TYPES.map(type => (
            <div key={type.value} className="flex items-center gap-1 text-xs text-white/50">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
              {type.label}
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        /* Layout: Calendar on left, Events on right */
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar - Takes 2 columns */}
          <div className="lg:col-span-2">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-4 bg-[#1C1C1C] border border-white/10 mb-0">
              <button onClick={prevMonth} className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button onClick={nextMonth} className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="bg-[#1C1C1C] border border-white/10 border-t-0 overflow-hidden">
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-white/10">
                {dayNames.map(day => (
                  <div key={day} className="p-2 text-center text-white/50 text-xs font-medium">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Days grid */}
              <div className="grid grid-cols-7">
                {daysInMonth.map((day, index) => {
                  const dayEvents = day ? getEventsForDay(day) : [];
                  const isToday = day && day.toDateString() === new Date().toDateString();
                  const cellKey = day ? day.toISOString() : `empty-${index}`;

                  return (
                    <div
                      key={cellKey}
                      className={`min-h-24 p-1 border-b border-r border-white/5 ${
                        !day ? 'bg-white/5' : ''
                      } ${isToday ? 'bg-[#D4AF37]/5' : ''}`}
                    >
                      {day && (
                        <>
                          <div className={`text-xs mb-1 ${
                            isToday ? 'text-[#D4AF37] font-bold' : 'text-white/50'
                          }`}>
                            {day.getDate()}
                          </div>
                          <div className="space-y-0.5">
                            {dayEvents.slice(0, 2).map(event => {
                              const typeInfo = getEventTypeInfo(event);
                              const timeStr = getEventTime(event);
                              return (
                                <button
                                  key={event.id}
                                  onClick={() => openEventDialog(event)}
                                  className="w-full text-left p-0.5 rounded text-[10px] truncate hover:opacity-80 transition"
                                  style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}
                                  data-testid={`event-${event.id}`}
                                >
                                  {timeStr && <span className="font-bold">{timeStr} </span>}
                                  <span className="font-medium">{getEventDisplayLabel(event)}</span>
                                </button>
                              );
                            })}
                            {dayEvents.length > 2 && (
                              <div className="text-[10px] text-white/40 text-center">
                                +{dayEvents.length - 2}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar: Events to validate + Upcoming */}
          <div className="space-y-4">
            {/* Events to validate */}
            <div className="card-dashboard max-h-[400px] overflow-y-auto">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2 sticky top-0 bg-[#1C1C1C] py-1">
                <Clock className="w-4 h-4 text-yellow-500" />
                À valider ({events.filter(e => e.status === 'proposed').length})
              </h3>
              {events.filter(e => e.status === 'proposed').length === 0 ? (
                <p className="text-white/40 text-center py-4 text-sm">Aucune date en attente</p>
              ) : (
                <div className="space-y-2">
                  {events.filter(e => e.status === 'proposed').map(event => {
                    const typeInfo = getEventTypeInfo(event);
                    const timeStr = getEventTime(event);
                    return (
                      <div key={event.id} className="p-2 bg-[#0A0A0A] border border-white/10">
                        <div className="flex items-start gap-2">
                          <div 
                            className="w-1.5 h-8 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: typeInfo.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{getEventDisplayLabel(event)}</p>
                            <p className="text-white/50 text-xs truncate">{event.client_name}</p>
                            <p className="text-[#D4AF37] text-xs">
                              {new Date(event.proposed_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                              {timeStr && ` à ${timeStr}`}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleValidateEvent(event.id)}
                              className="p-1 text-green-400 hover:bg-green-500/10 rounded"
                              title="Valider"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEventDialog(event)}
                              className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Upcoming validated events */}
            <div className="card-dashboard max-h-[300px] overflow-y-auto">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2 sticky top-0 bg-[#1C1C1C] py-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Confirmés
              </h3>
              {events.filter(e => e.status === 'validated').length === 0 ? (
                <p className="text-white/40 text-center py-4 text-sm">Aucune date confirmée</p>
              ) : (
                <div className="space-y-2">
                  {events.filter(e => e.status === 'validated').slice(0, 5).map(event => {
                    const typeInfo = getEventTypeInfo(event);
                    const timeStr = getEventTime(event);
                    return (
                      <div key={event.id} className="p-2 bg-[#0A0A0A] border border-white/10">
                        <div className="flex items-start gap-2">
                          <div 
                            className="w-1.5 h-8 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: typeInfo.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{getEventDisplayLabel(event)}</p>
                            <p className="text-white/50 text-xs truncate">{event.client_name}</p>
                            <p className="text-green-400 text-xs">
                              {new Date(event.validated_date || event.proposed_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                              {timeStr && ` à ${timeStr}`}
                            </p>
                          </div>
                          <button
                            onClick={() => openEventDialog(event)}
                            className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded"
                            title="Voir/Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <span>{selectedEvent && getEventDisplayLabel(selectedEvent)}</span>
              <button
                onClick={() => selectedEvent && handleDeleteEvent(selectedEvent.id)}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition"
                title="Supprimer l'événement"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedEvent?.quote_id && (
              <div className="p-3 bg-[#0A0A0A] border border-white/10">
                <p className="text-white/50 text-sm">Devis</p>
                <Link 
                  to={`/admin/devis/${selectedEvent?.quote_id}`}
                  className="text-[#D4AF37] hover:underline"
                >
                  {selectedEvent?.quote_number}
                </Link>
                <p className="text-white mt-1">{selectedEvent?.client_name}</p>
                <p className="text-white/50 text-sm">{selectedEvent?.pro_company_name}</p>
              </div>
            )}

            <div>
              <label className="text-white/60 text-sm mb-2 block">Date proposée</label>
              <p className="text-white">
                {selectedEvent?.proposed_date && new Date(selectedEvent.proposed_date).toLocaleDateString('fr-FR')}
                {selectedEvent?.proposed_date?.includes('T') && (
                  <span className="ml-2 text-white/60">
                    {selectedEvent.proposed_date.split('T')[1]?.substring(0, 5)}
                  </span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                  Date validée / modifiée
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={editForm.validated_date || ''}
                    onChange={(e) => setEditForm({...editForm, validated_date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  Heure
                </label>
                <div className="relative">
                  <Input
                    type="time"
                    value={editForm.validated_time || ''}
                    onChange={(e) => setEditForm({...editForm, validated_time: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                  />
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Statut</label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10">
                  <SelectItem value="proposed" className="text-white hover:bg-white/10">Date proposée</SelectItem>
                  <SelectItem value="validated" className="text-white hover:bg-white/10">Date validée</SelectItem>
                  <SelectItem value="modified_by_admin" className="text-white hover:bg-white/10">Modifiée par admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Commentaire</label>
              <Input
                value={editForm.comment}
                onChange={(e) => setEditForm({...editForm, comment: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                placeholder="Ajouter un commentaire..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button onClick={() => setIsDialogOpen(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button onClick={handleUpdateEvent} className="btn-primary flex-1">
                Enregistrer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Ajouter un événement</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Type d'événement *</label>
              <Select 
                value={newEventForm.event_type} 
                onValueChange={(v) => setNewEventForm({...newEventForm, event_type: v, event_label: ''})}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10">
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newEventForm.event_type === 'autre' && (
              <div>
                <label className="text-white/60 text-sm mb-2 block">Préciser le type *</label>
                <Input
                  value={newEventForm.event_label}
                  onChange={(e) => setNewEventForm({...newEventForm, event_label: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                  placeholder="Ex: SAV, Contrôle chantier, Retouche..."
                />
                <p className="text-white/40 text-xs mt-1">Ce champ est obligatoire pour le type "Autre"</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                  Date *
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={newEventForm.proposed_date}
                    onChange={(e) => setNewEventForm({...newEventForm, proposed_date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  Heure
                </label>
                <div className="relative">
                  <Input
                    type="time"
                    value={newEventForm.proposed_time}
                    onChange={(e) => setNewEventForm({...newEventForm, proposed_time: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                  />
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Associer à un dossier (facultatif)</label>
              <Select 
                value={newEventForm.quote_id} 
                onValueChange={(v) => setNewEventForm({...newEventForm, quote_id: v})}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue placeholder="Sans dossier" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10 max-h-60">
                  <SelectItem value="none" className="text-white hover:bg-white/10">Sans dossier</SelectItem>
                  {quotes.map(quote => (
                    <SelectItem key={quote.id} value={quote.id} className="text-white hover:bg-white/10">
                      {quote.quote_number} - {quote.client_info?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Commentaire</label>
              <Input
                value={newEventForm.comment}
                onChange={(e) => setNewEventForm({...newEventForm, comment: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                placeholder="Ajouter un commentaire..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button onClick={() => setIsAddDialogOpen(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button onClick={handleAddEvent} className="btn-primary flex-1">
                Créer l'événement
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
